import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { jwtDecode } from 'jwt-decode';
import React, { createContext, Dispatch, useReducer } from "react";

const AUTH_KEY = "auth";

export type AuthInfo = {
  username: string;
  bearerToken: string;
};

type AuthContextData = {
  authInfo?: AuthInfo;
  authApiClient?: AxiosInstance;
};

export type AuthInfoAction = {
  type: "SET" | "DELETE";
  authInfo?: AuthInfo;
};

type AuthProviderProps = {
  children: JSX.Element | JSX.Element[];
};

export const AuthContext = createContext<AuthContextData | undefined>(
  undefined,
);
export const AuthDispatchContext = createContext<
  Dispatch<AuthInfoAction> | undefined
>(undefined);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const authString = localStorage.getItem(AUTH_KEY);
  let startupAuthContext: AuthContextData | undefined;
  if (authString) {
    const auth = JSON.parse(authString) as AuthInfo;
    if (!isTokenExpired(auth.bearerToken)) {
      const client = createAuthApiClient(auth);
      startupAuthContext = {
        authInfo: auth,
        authApiClient: client,
      };
    } else {
      localStorage.removeItem(AUTH_KEY);
    }
  }
  const [authInfo, dispatch] = useReducer(authReducer, startupAuthContext);
  return (
    <AuthContext.Provider value={authInfo}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthContext.Provider>
  );
};

function authReducer(
  authContext: AuthContextData | undefined,
  action: AuthInfoAction,
): AuthContextData | undefined {
  switch (action.type) {
    case "SET": {
      if (action.authInfo) {
        localStorage.setItem(AUTH_KEY, JSON.stringify(action.authInfo));
        const client = createAuthApiClient(action.authInfo);
        return { authInfo: action.authInfo, authApiClient: client };
      }
      return authContext;
    }
    case "DELETE": {
      localStorage.removeItem(AUTH_KEY);
      return undefined;
    }
    default: {
      throw Error("Unknown action: " + action.type);
    }
  }
}

function createAuthApiClient(authInfo: AuthInfo): AxiosInstance {
  const client = axios.create({ baseURL: process.env.REACT_APP_API_URL });
  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    let token = authInfo.bearerToken;
    if (token && isTokenExpiringSoon(token)) {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await axios.post(`${baseUrl}/auth/refresh`);
      if (response.status === 200) {
        const data = response.data;
        token = data.token;
      }
      const newAuthInfo: AuthInfo = {username: authInfo.username, bearerToken: token}
      localStorage.setItem(AUTH_KEY, JSON.stringify(newAuthInfo));
    }
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  return client;
}


function isTokenExpiringSoon(bearerToken: string, minutesBeforeExpiry = 30): boolean {
  try {
    const decoded = jwtDecode(bearerToken);
    const expiryTime = (decoded.exp || 0) * 1000;
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;
    const threshold = minutesBeforeExpiry * 60 * 1000;
    
    return timeUntilExpiry < threshold;
  } catch (e) {
    return true;
  }
}


function isTokenExpired(bearerToken: string): boolean {
  try {
    const decoded = jwtDecode(bearerToken);
    const expiryTime = (decoded.exp || 0) * 1000;
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;
    
    return timeUntilExpiry < 0;
  } catch (e) {
    return true;
  }
}